import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Form, Button, Col, Row, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { GrLanguage } from "react-icons/gr";
import { MdKeyboardArrowLeft } from "react-icons/md";
import {
  changeSelectedTemplate,
  clearQRTemplateStore,
  getActiveTemplates,
  showLoader,
  getCategories,
} from "../../redux/actions/templateActions";
import { receiveProfileData } from "../../redux/actions/profileActions";
import { ShowForPermission } from "../../components/ShowForPermission";
import { checkPermission } from "../../redux/actions/permissionActions";
import { receiveMarketData } from "../../redux/actions/profileActions";
import jwtDecode from "jwt-decode";
import {
  QR_GENERATE_BULK,
  QR_GENERATE_INDIVIDUAL,
  QR_GENERATE_TEMPLATE_LIST,
} from "../../utils/permissionList";
import {
  SOMETHING_WENT_WRONG,
  UNAUTHORIZED_ACTION,
} from "../../utils/errorMessages";

const SelectTemplate = ({
  templates = [],
  selectTemplate,
  userGroups,
  isLoading,
  getTemplateList,
  checkPermission,
  isSuperAdmin,
  marketOptions = [],
  clearStore,
  receiveMarketData,
  receiveProfile,
  categories = [],
  getCategories,
}) => {
  const history = useHistory();

  const [selectedMarket, setSelectedMarket] = useState("kenya");
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    isLoading(true);
    // fetch templates (active only) for market
    getTemplateList(selectedMarket, true)
      .then(() => {
        isLoading(false);
      })
      .catch((e) => {
        isLoading(false);
        if (e.response?.status === 403) {
          toast.error(UNAUTHORIZED_ACTION);
        } else if (e.response?.data?.Description) {
          toast.error(e.response.data.Description);
        } else {
          toast.error(SOMETHING_WENT_WRONG);
        }
      });

    // fetch categories for market (active)
    getCategories(selectedMarket, null, false, true);
  }, [selectedMarket, getTemplateList, isLoading, getCategories]);

  useEffect(() => {
    receiveMarketData();
    receiveProfile(jwtDecode(localStorage.id_token));
  }, [receiveMarketData, receiveProfile]);

  // clear the store on unmount
  useEffect(() => () => clearStore(), [clearStore]);

  const goToNext = async (template) => {
    isLoading(true);
    const hasPermissions = await checkPermission([
      QR_GENERATE_BULK,
      QR_GENERATE_INDIVIDUAL,
    ]);
    if (hasPermissions) {
      selectTemplate({ ...template, isActive: true });
      isLoading(false);
      history.push("/generate-qr/fill-params");
    } else {
      isLoading(false);
      toast.error("You don't have permission to Generate QR Codes");
    }
  };

  // Render category cards (active categories only)
  const renderCategories = categories
    .filter((c) => c.isActive)
    .map((category) => {
      const templatesImages = templates
        .filter((t) => t.templateCategoryId === category.id && t.isActive)
        .map((t) => t.pngTemplatePath);

      return templatesImages.length > 0 && (
        <Col lg={3} md={6} sm={6} key={category.id}>
          <Card
            id={category.id + "-" + category.name}
            onClick={() => setSelectedCategory(category)}
          >
            <div className="wrap-img" style={{ backgroundColor: "#FFF" }}>
              <Row className="mx-0">
                {templatesImages.slice(0, 6).map((img, index) => (
                  <Col
                    key={index}
                    xs={4}
                    className="d-flex justify-content-center p-2"
                  >
                    <Card className="d-flex justify-content-center align-items-center shadow-sm mb-0">
                      <Card.Body className="d-flex justify-content-center align-items-center">
                        <img
                          src={img}
                          alt={`Icon ${index + 1}`}
                          width={50}
                          height={50}
                          style={{ objectFit: "contain" }}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
            <Card.Body className="d-flex justify-content-between" style={{ padding: "22px" }}>
              <Card.Text id={category.id + "-" + category.name + "-name"}>
                {category.name}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      );
    });

  // Render templates filtered by selectedCategory (or uncategorized when none selected)
  const renderTemplates = templates
    .filter((t) => {
      if (selectedCategory === null) {
        return (
          (t.templateCategoryId === null || t.templateCategoryId === "") &&
          t.isActive
        );
      }
      return t.templateCategoryId === selectedCategory.id && t.isActive;
    })
    .map((template) => {
      return (
        <Col lg={3} md={6} sm={6} key={template.id}>
          <Card onClick={() => goToNext(template)} id={template.id + "-" + template.language}>
            <div className="wrap-img">
              <Card.Img
                variant="top"
                src={template.pngTemplatePath}
                alt={template.templateName}
              />
            </div>
            <Card.Body className="d-flex justify-content-between">
              <Card.Text id={template.id + "-" + template.language + "-name"}>
                {template.templateName}
              </Card.Text>
              {template.isDefault ? (
                <Button
                  data-testid="cancelButton"
                  variant="primary"
                  className="btn-cancel"
                  id={"btn-" + template.id + "-" + template.language + "-lang"}
                >
                  <span>Default</span>
                </Button>
              ) : (
                <Button
                  data-testid="cancelButton"
                  variant="primary"
                  className="btn-cancel"
                  id={"btn-" + template.id + "-" + template.language + "-lang"}
                >
                  <GrLanguage className="lcon-lang" />
                  <span className="txt-lan">{template.language}</span>
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      );
    });

  return (
    <div className="page qr-genarate-page">
      <div className="page-title-row d-flex justify-content-between">
        <div className="title">
          <div className="d-flex align-items-baseline">
              <h1 className="mb-0">Generate QR</h1>
              {selectedCategory != null && <h6 className="mb-0 ms-2 fw-normal text-muted">/ {selectedCategory.name}</h6>}
            </div>
          <div className="subtitle-1">
            Please select one of the following templates to generate static QR
            Code(s)
          </div>
          {selectedCategory != null && <div className="frm-btn btn-section">
            <Button
              data-testid="backButton"
              variant="primary"
              className="btn-link back-btn"
              onClick={() => setSelectedCategory(null)}
              name="back"
            >
              <MdKeyboardArrowLeft />
              Back
            </Button>
          </div>}
        </div>

        {selectedCategory === null && <div className="my-1 mx-2">
          {isSuperAdmin ? (
            <Form.Select
              style={{ paddingRight: "36px" }}
              id="marketSelect"
              value={selectedMarket}
              onChange={(e) => setSelectedMarket(e.target.value)}
            >
              {marketOptions.map((market) => {
                return (
                  <option key={market.value} value={market.value}>
                    {market.displayName}
                  </option>
                );
              })}
              <option value="general">General</option>
            </Form.Select>
          ) : (
            <Form.Select
              style={{ paddingRight: "36px" }}
              id="marketSelect"
              value={selectedMarket}
              onChange={(e) => setSelectedMarket(e.target.value)}
            >
              {marketOptions
                ? marketOptions.length > 0
                  ? marketOptions
                      .filter((x) => userGroups && userGroups.includes(x.userGroup))
                      .map((y) => {
                        return (
                          <option key={y.value} value={y.value}>
                            {y.displayName}
                          </option>
                        );
                      })
                  : null
                : null}
              <option value="general">General</option>
            </Form.Select>
          )}
        </div>}
      </div>

      <ShowForPermission permissions={[QR_GENERATE_TEMPLATE_LIST]} showBanner>
        <div className="card-set-qr">
          <Row>
            {selectedCategory === null && renderCategories}
            {renderTemplates}
          </Row>
        </div>
      </ShowForPermission>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    templates: state.templates.list,
    userGroups: state.profile?.groups,
    isSuperAdmin: state.permission.isSuperAdmin,
    marketOptions: state.profile.markets,
    categories: state.templates.categories,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getTemplateList: (market, isActive) =>
    dispatch(getActiveTemplates(market, isActive)),
  selectTemplate: (data) => dispatch(changeSelectedTemplate(data)),
  isLoading: (flag) => dispatch(showLoader(flag)),
  checkPermission: (permissions) => dispatch(checkPermission(permissions)),
  clearStore: () => dispatch(clearQRTemplateStore()),
  receiveMarketData: () => dispatch(receiveMarketData()),
  receiveProfile: (data) => dispatch(receiveProfileData(data)),
  getCategories: (market, name, isArchive, isActive) =>
    dispatch(getCategories(market, name, isArchive, isActive)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectTemplate);
